import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Webcam from 'react-webcam';
import { I18n } from '@/i18n/I18n';
import { CameraDevice, useCameraDevices } from '@/frontend/hooks/useCameraDevices';
import { LoadingSpinner } from '../LoadingSpinner';

/**
 * Choosing which camera an equipment item uses.
 *
 * Ported from veriform-desktop's `CameraConnectionConfig` (Equipment/). A
 * camera has no provider schema -- its configuration is which device to point
 * at -- so this stands in for the field table the other providers get.
 *
 * What is stored is `{ groupId, label }`, not a device id: ids are
 * per-origin and per-session, so one saved here would mean nothing later.
 * `label` is the part that matters -- desktop matches it against ffmpeg's own
 * device list when it captures. `groupId` is a first-try hint for re-selecting
 * the device, and will not match across origins, which is why desktop falls
 * back to the label.
 */

function findDevice(devices: CameraDevice[], groupId?: string, label?: string) {
  return (groupId && devices.find(device => device.groupId === groupId))
    || (label && devices.find(device => device.label === label))
    || undefined;
}

export function CameraConnectionConfig(props: {
  storedConfiguration?: string;
  setConfig: (configuration: string) => void;
}) {
  const { storedConfiguration, setConfig } = props;
  const { devices, loading, denied } = useCameraDevices(true);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const initialised = useRef(false);

  const choose = (device: CameraDevice) => {
    setSelectedDeviceId(device.deviceId);
    setConfig(JSON.stringify({ groupId: device.groupId, label: device.label }));
  };

  // The saved device if it is still attached, otherwise the first one, so the
  // dialog always has something selected to preview.
  useEffect(() => {
    if (initialised.current || devices.length === 0) return;

    const saved = parseConfiguration(storedConfiguration);
    const device = findDevice(devices, saved.groupId, saved.label) ?? devices[0];

    initialised.current = true;
    setSelectedDeviceId(device.deviceId);
    setConfig(JSON.stringify({ groupId: device.groupId, label: device.label }));
  }, [devices, storedConfiguration, setConfig]);

  // A camera unplugged while the dialog is open.
  useEffect(() => {
    if (!initialised.current || !selectedDeviceId) return;
    if (devices.some(device => device.deviceId === selectedDeviceId)) return;

    if (devices.length === 0) {
      setSelectedDeviceId('');
      return;
    }
    choose(devices[0]);
    // `choose` is rebuilt each render; the device list is what changes.
  }, [devices, selectedDeviceId]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-3">
      <Form.Group className="mb-3">
        <Form.Label htmlFor="camera-device">{I18n.get('selectCameraDevice')}</Form.Label>
        <Form.Select
          id="camera-device"
          aria-label={I18n.get('selectCameraDevice')}
          value={selectedDeviceId}
          onChange={event => {
            const device = devices.find(({ deviceId }) => deviceId === event.target.value);
            if (device) choose(device);
          }}
        >
          { devices.length === 0
            ? <option>{I18n.get('noCameraDevices')}</option>
            : devices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>{device.label}</option>
            )) }
        </Form.Select>
        { devices.length === 0 && (
          <Form.Text className="text-muted">
            { I18n.get(denied ? 'cameraPermissionNeeded' : 'noCameraDevicesHint') }
          </Form.Text>
        )}
      </Form.Group>

      { selectedDeviceId && (
        <div className="d-flex align-items-center justify-content-center mb-3" style={{ width: '100%' }}>
          <Webcam
            audio={false}
            videoConstraints={{ deviceId: selectedDeviceId, height: 480, width: 640 }}
            screenshotFormat="image/jpeg"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
      )}
    </div>
  );
}

function parseConfiguration(configuration?: string): { groupId?: string; label?: string } {
  if (!configuration) return {};
  try {
    return JSON.parse(configuration);
  } catch {
    return {};
  }
}
